import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import Modal from "antd/lib/modal";
import DatePicker from "antd/lib/date-picker";
import TimePicker from "antd/lib/time-picker";
import Select from "antd/lib/select";
import Radio from "antd/lib/radio";
import { capitalize, clone, isEqual, omitBy, isNil, isEmpty } from "lodash";
import moment from "moment";
import { secondsToInterval, durationHumanize, pluralize, IntervalEnum, localizeTime } from "@/lib/utils";
import { wrap as wrapDialog, DialogPropType } from "@/components/dialog-wrapper/DialogWrapper";
import { RefreshScheduleType, RefreshScheduleDefault, Moment } from "../proptypes";
import { useTheme } from "@/components/ThemeProvider";

import "./ScheduleDialog.less";

const WEEKDAYS_SHORT = moment.weekdaysShort();
const WEEKDAYS_FULL = moment.weekdays();
const DATE_FORMAT = "YYYY-MM-DD";
const HOUR_FORMAT = "HH:mm";
const { Option, OptGroup } = Select;

// Move getInitialState outside of the component
const getInitialState = (schedule) => {
  const newSchedule = clone(schedule || RefreshScheduleDefault);
  const { time, interval: seconds, day_of_week: day } = newSchedule;
  const { interval } = secondsToInterval(seconds);
  const [hour, minute] = time ? localizeTime(time).split(":") : [null, null];

  return {
    hour,
    minute,
    seconds,
    interval,
    dayOfWeek: day ? WEEKDAYS_SHORT[WEEKDAYS_FULL.indexOf(day)] : null,
    newSchedule,
  };
};

export function TimeEditor(props) {
  const [time, setTime] = useState(props.defaultValue);
  const showUtc = time && !time.isUTC();

  function onChange(newTime) {
    setTime(newTime);
    props.onChange(newTime);
  }

  return (
    <React.Fragment>
      <TimePicker allowClear={false} value={time} format={HOUR_FORMAT} minuteStep={5} onChange={onChange} />
      {showUtc && (
        <span className="utc" data-testid="utc">
          ({moment.utc(time).format(HOUR_FORMAT)} UTC)
        </span>
      )}
    </React.Fragment>
  );
}

TimeEditor.propTypes = {
  defaultValue: Moment,
  onChange: PropTypes.func.isRequired,
};

TimeEditor.defaultProps = {
  defaultValue: null,
};

function ScheduleDialog({ dialog, schedule, refreshOptions }) {
  const [state, setState] = useState(() => getInitialState(schedule));
  const { isDarkMode } = useTheme();

  const getIntervals = () => {
    const ret = {
      [IntervalEnum.NEVER]: [],
    };
    refreshOptions.forEach(seconds => {
      const { count, interval } = secondsToInterval(seconds);
      if (!(interval in ret)) {
        ret[interval] = [];
      }
      ret[interval].push([count, seconds]);
    });

    return ret;
  };

  const intervals = useMemo(getIntervals, [refreshOptions]);

  const setTime = time => {
    setState(prevState => ({
      ...prevState,
      newSchedule: {
        ...prevState.newSchedule,
        time: moment(time)
          .utc()
          .format(HOUR_FORMAT),
      },
    }));
  };

  const setInterval = newSeconds => {
    const { newSchedule } = state;
    const { interval: newInterval } = secondsToInterval(newSeconds);

    // resets to defaults
    if (newInterval === IntervalEnum.NEVER) {
      newSchedule.until = null;
    }
    if ([IntervalEnum.NEVER, IntervalEnum.MINUTES, IntervalEnum.HOURS].indexOf(newInterval) !== -1) {
      newSchedule.time = null;
    }
    if (newInterval !== IntervalEnum.WEEKS) {
      newSchedule.day_of_week = null;
    }
    if (
      (newInterval === IntervalEnum.DAYS || newInterval === IntervalEnum.WEEKS) &&
      (!state.minute || !state.hour)
    ) {
      newSchedule.time = moment()
        .hour("00")
        .minute("15")
        .utc()
        .format(HOUR_FORMAT);
    }
    if (newInterval === IntervalEnum.WEEKS && !state.dayOfWeek) {
      newSchedule.day_of_week = WEEKDAYS_FULL[0];
    }

    newSchedule.interval = newSeconds;

    const [hour, minute] = newSchedule.time ? localizeTime(newSchedule.time).split(":") : [null, null];

    setState(prevState => ({
      ...prevState,
      interval: newInterval,
      seconds: newSeconds,
      hour,
      minute,
      dayOfWeek: newSchedule.day_of_week ? WEEKDAYS_SHORT[WEEKDAYS_FULL.indexOf(newSchedule.day_of_week)] : null,
      newSchedule: newSchedule,
    }));
  };

  const setScheduleUntil = (_, date) => {
    setState(prevState => ({
      ...prevState,
      newSchedule: { ...prevState.newSchedule, until: date },
    }));
  };

  const setWeekday = e => {
    const dayOfWeek = e.target.value;
    setState(prevState => ({
      ...prevState,
      dayOfWeek,
      newSchedule: {
        ...prevState.newSchedule,
        day_of_week: dayOfWeek ? WEEKDAYS_FULL[WEEKDAYS_SHORT.indexOf(dayOfWeek)] : null,
      },
    }));
  };

  const setUntilToggle = e => {
    const date = e.target.value ? moment().format(DATE_FORMAT) : null;
    setScheduleUntil(null, date);
  };

  const save = () => {
    const { newSchedule } = state;
    const hasChanged = () => {
      const newCompact = omitBy(newSchedule, isNil);
      const oldCompact = omitBy(schedule, isNil);
      return !isEqual(newCompact, oldCompact);
    };

    if (hasChanged()) {
      if (newSchedule.interval) {
        dialog.close(clone(newSchedule));
      } else {
        dialog.close(null);
      }
    }
    dialog.dismiss();
  };

  return (
    <Modal 
      {...dialog.props} 
      title="Refresh Schedule" 
      className={`schedule ${isDarkMode ? 'dark-mode' : ''}`} 
      onOk={() => save()}
    >
      <div className="schedule-component">
        <h5>Refresh every</h5>
        <div data-testid="interval">
          <Select 
            className={`input ${isDarkMode ? 'dark-mode' : ''}`} 
            value={state.seconds} 
            onChange={setInterval} 
            dropdownMatchSelectWidth={false}
          >
            <Option value={null} key="never">
              Never
            </Option>
            {Object.keys(intervals)
              .filter(int => !isEmpty(intervals[int]))
              .map(int => (
                <OptGroup label={capitalize(pluralize(int))} key={int}>
                  {intervals[int].map(([cnt, secs]) => (
                    <Option value={secs} key={`${int}-${cnt}`}>
                      {durationHumanize(secs)}
                    </Option>
                  ))}
                </OptGroup>
              ))}
          </Select>
        </div>
      </div>
      {[IntervalEnum.DAYS, IntervalEnum.WEEKS].indexOf(state.interval) !== -1 ? (
        <div className="schedule-component">
          <h5>On time</h5>
          <div data-testid="time">
            <TimeEditor
              defaultValue={
                state.hour
                  ? moment()
                      .hour(state.hour)
                      .minute(state.minute)
                  : null
              }
              onChange={setTime}
            />
          </div>
        </div>
      ) : null}
      {IntervalEnum.WEEKS === state.interval ? (
        <div className="schedule-component">
          <h5>On day</h5>
          <div data-testid="weekday">
            <Radio.Group size="medium" defaultValue={state.dayOfWeek} onChange={setWeekday}>
              {WEEKDAYS_SHORT.map(day => (
                <Radio.Button value={day} key={day} className="input">
                  {day[0]}
                </Radio.Button>
              ))}
            </Radio.Group>
          </div>
        </div>
      ) : null}
      {state.interval !== IntervalEnum.NEVER ? (
        <div className="schedule-component">
          <h5>Ends</h5>
          <div className="ends" data-testid="ends">
            <Radio.Group size="medium" value={!!state.until} onChange={setUntilToggle}>
              <Radio value={false}>Never</Radio>
              <Radio value>On</Radio>
            </Radio.Group>
            {state.until ? (
              <DatePicker
                size="small"
                className="datepicker"
                value={moment(state.until)}
                allowClear={false}
                format={DATE_FORMAT}
                onChange={setScheduleUntil}
              />
            ) : null}
          </div>
        </div>
      ) : null}
    </Modal>
  );
}

ScheduleDialog.propTypes = {
  dialog: DialogPropType.isRequired,
  schedule: RefreshScheduleType,
  refreshOptions: PropTypes.arrayOf(PropTypes.number).isRequired,
};

ScheduleDialog.defaultProps = {
  schedule: RefreshScheduleDefault,
};

export default wrapDialog(ScheduleDialog);
